import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { SubscriptionService, ReportExportService } from "../../api/services";
import {
  UserState,
  US_ReportEmbeddedType,
  US_ReportEmbededExport,
} from "../../api/models";
import { Router } from "@angular/router";
import { GlobalApp } from "../../tools/global";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogResultType } from "../../dialogs/DialogResult";
import { Subscription } from "rxjs";
import { PowerbiReportEmbedComponent, ReportEmbeddedResult } from "../../elements/powerbi-report-embed/powerbi-report-embed.component";
import { MessageDialogComponent } from "../../dialogs/message-dialog/message-dialog.component";
import { Clipboard } from "@angular/cdk/clipboard";
import { GuidService } from "../../services/guid-service";

@Component({
  selector: "widgets",
  templateUrl: "./widgets.component.html",
})
export class WidgetsComponent implements OnInit, OnDestroy {

  @ViewChild(PowerbiReportEmbedComponent) powerBi : PowerbiReportEmbedComponent;
  showPowerBi: boolean = false;

  userState: UserState = {
    reportOnly: false,
    userManagerOnly: false,
    invoiceManagerOnly: false,
    interactives: [],
    modifiedInteractives: [],
    xmlas: [],
    reportExportState: {},
    isInteractiveUser: false,
    isReportExportUser: false,
    isEmbeddedUser: false,
    hasAiChats: false,
    isValidState: false
  };
  userStateSubscription: Subscription = null;

  public loadingData = false;

  public reportEmbeddedTypes: Array<US_ReportEmbeddedType>;

  public reportEmbeds: Array<US_ReportEmbededExport>;
  public filteredReportEmbeds: Array<US_ReportEmbededExport>;

  public selectedEmbeddedReport: US_ReportEmbeddedType = undefined;

  public isAdmin = false;

  constructor(
    private reportExportService: ReportExportService,
    private subscriptionService: SubscriptionService,
    private app: GlobalApp,
    private router: Router,
    private dialog: MatDialog,
    private clipboard: Clipboard,
  ) {
    this.app.getAuthorizationStatus().subscribe((result) => {
      if (!result.isValid) {
        this.app.logOut();
        this.router.navigate(["login"]);
      }
    });
  }

  loadUserState() {
    this.loadingData = true;
    this.app.refreshUserState(this.subscriptionService);
  }

  ngOnInit() {
    this.userStateSubscription = this.app.userState.subscribe((userState) => {
      this.userState = userState;

      this.reportExportService.reportExportGetUserReportExportState().subscribe(reportExportState => {
        const lastStatus = this.app.getLastStatus();

        this.userState.reportExportState = reportExportState;

        this.reportEmbeddedTypes = this.userState.reportExportState.reportEmbeddedTypes;
        this.reportEmbeds = this.userState.reportExportState.reportEmbeds

        if (this.userState?.reportExportState?.reportEmbeddedTypes?.length > 0) {
          if (lastStatus.lastEditReportEmbeddedTypeId) {
            this.selectedEmbeddedReport = this.userState.reportExportState.reportEmbeddedTypes.find(x => x.reportEmbeddedTypeId === lastStatus.lastEditReportEmbeddedTypeId);
          } else {
            this.selectedEmbeddedReport = this.userState.reportExportState.reportEmbeddedTypes[0];
          }

          this.embeddedReportChanged();
        }

        this.loadingData = false;
      });
    });

    this.loadUserState();

    this.app.isAdmin.subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
    });
  }

  ngOnDestroy() {
    if (this.userStateSubscription) {
      this.userStateSubscription.unsubscribe();
    }
  }

  embeddedReportChanged() {
    if(this.selectedEmbeddedReport) {
      this.app.setLastEditReportEmbeddedType(this.selectedEmbeddedReport.reportEmbeddedTypeId);
      this.filteredReportEmbeds = this.reportEmbeds.filter(x => x.reportEmbeddedTypeId === this.selectedEmbeddedReport.reportEmbeddedTypeId);
    }
  }

  createNewEmbeddedReport() {
    var newEmbeddedExport: US_ReportEmbededExport = {
      reportEmbeddedExportId: -1,
      reportEmbeddedTypeId: this.selectedEmbeddedReport.reportEmbeddedTypeId,
      subscriptionId: -1,
      userId: -1,
      filterData: undefined,
      name: '<New Widget>',
      reportPage: this.selectedEmbeddedReport.reportPages.split(",")[0],
      created: '',
      lastUpdate: '',
      embeddToken: '',
      disabled: false,
      canEdit: true,
      gatedOnly: true
    }

    var reportSource = this.userState.reportExportState.reportSources.find(x => x.reportSourceId == this.selectedEmbeddedReport.reportSourceId);

    this.powerBi.loadEmbeddedState(true, undefined, newEmbeddedExport, this.userState.preFilters, reportSource, this.selectedEmbeddedReport, true, false, this.userState);
    this.showPowerBi = true;

    return false;
  }

  editEmbed(embed: US_ReportEmbededExport, clearBookmark: boolean) {
    var reportType = this.userState.reportExportState.reportEmbeddedTypes.find(x => x.reportEmbeddedTypeId == embed.reportEmbeddedTypeId);
    var reportSource = this.userState.reportExportState.reportSources.find(x => x.reportSourceId == reportType.reportSourceId);

    this.powerBi.loadEmbeddedState(true, undefined, embed, this.userState.preFilters, reportSource, reportType, false, clearBookmark, this.userState);
    this.showPowerBi = true;

    return false;
  }

  deleteEmbed(embed: US_ReportEmbededExport) {

    ConfirmDialogComponent.showDialog(
      this.dialog,
      "Confirm Delete Widget?",
      "This will permanately delete this widget, are you sure?"
    ).subscribe((confirmResult) => {
      if (confirmResult.ResultType === DialogResultType.Ok) {
        this.reportExportService.reportExportReportEmbeddedExportDelete(embed.reportEmbeddedExportId).subscribe((_) => {
          MessageDialogComponent.showDialog(this.dialog, "Success", "Embedded Report Deleted.").subscribe((_) => { });
          this.loadUserState();
        });
      }
    });

    return false;
  }

  copyEmbedUrl(embed: US_ReportEmbededExport) {
    this.clipboard.copy(embed.embedUrl);
    MessageDialogComponent.showDialog(
      this.dialog,
      "Success",
      "'" + embed.embedUrl + "' copied to clipboard."
    );

    return false;
  }

  private getGatedUrl(embed: US_ReportEmbededExport): string {
    var shiftedGuid = GuidService.ShiftGuid(embed.embeddToken);
    var newUrl = embed.embedUrl.replace('report-embed', 'report-embed-public');
    newUrl = newUrl.replace(embed.embeddToken, shiftedGuid);
    return newUrl;
  }

  copyPublicEmbedUrl(embed: US_ReportEmbededExport) {
    var newUrl = this.getGatedUrl(embed);
    this.clipboard.copy(newUrl);
    MessageDialogComponent.showDialog(
      this.dialog,
      "Success",
      "'" + newUrl + "' copied to clipboard."
    );

    return false;
  }

  launchEmbed(embed: US_ReportEmbededExport) {
    window.open(this.getGatedUrl(embed), "_blank");
    return false;
  }

  closeClicked() {
    this.showPowerBi = false;
    return false;
  }

  saveClicked() {
    this.app.pushLoading("Saving...");

    this.powerBi.getEmbeddedResults().then((results: ReportEmbeddedResult) => {
      if (results.reportEmbeddedExportId == -1) {
        this.reportExportService.reportExportCreateReportEmbeddedExport({
          reportEmbeddedTypeId: results.reportEmbeddedTypeId,
          editData: {
            subscriptionId: results.subscriptionId,
            reportPage: results.reportPage,
            preFilterId: results.preFilterId,
            name: results.name,
            filterData: results.filterData,
            disabled: results.disabled
          }
        }).subscribe((_) => {
          this.reportExportService.reportExportGetUserReportExportState().subscribe((updatedState) => {
            this.userState.reportExportState.reportEmbeds = updatedState.reportEmbeds;
            this.reportEmbeds = updatedState.reportEmbeds;
            this.embeddedReportChanged();
            this.app.popLoading();
          });
        })
      } else {
        this.reportExportService.reportExportUpdateReportEmbeddedExport({
          reportEmbeddedExportId: results.reportEmbeddedExportId,
          editData: {
            subscriptionId: results.subscriptionId,
            reportPage: results.reportPage,
            preFilterId: results.preFilterId,
            name: results.name,
            filterData: results.filterData,
            disabled: results.disabled
          }
        }).subscribe((updatedEmbed) => {
          this.reportExportService.reportExportGetUserReportExportState().subscribe((updatedState) => {
            this.userState.reportExportState.reportEmbeds = updatedState.reportEmbeds;
            this.reportEmbeds = updatedState.reportEmbeds;
            this.embeddedReportChanged();
            this.app.popLoading();
          });
        })
      }
    });

    this.showPowerBi = false;
    return false;
  }
}
